import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { ActionsButton } from '../../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../../actions-button/actions-button.types';
import { StatusBadge } from '../../../status-badge/status-badge.component';
import { useLockerDetailsPageHeaderStyles } from './locker-details-page-header.styles';
import { ArrowDownward } from '@mui/icons-material';
import { SVGIconComponent } from '../../../icons/types';

export interface LockerDetailsPageHeaderProps {
  title: string;
  actions: LoadedActionsButtonItem[];
  subtitle?: string;
  status: {
    label: string;
    bgColor: string;
    Icon: SVGIconComponent;
  };
  availability?: {
    label: string;
    bgColor: string;
    color: string;
    Icon: SVGIconComponent;
  };
  size: {
    label: string;
    Icon?: SVGIconComponent;
  };
  isConsideredLow?: boolean;
  transactionTypes?: { Icon: SVGIconComponent; label: string }[];
  reservedForRecipients?: string[];
}

export function LockerDetailsPageHeader({
  title,
  subtitle,
  status,
  availability,
  size,
  isConsideredLow,
  transactionTypes,
  reservedForRecipients,
  actions
}: LockerDetailsPageHeaderProps) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useLockerDetailsPageHeaderStyles();
  return (
    <div className={classes.outerWrapper}>
      <Grid container sx={{ flexDirection: isXS ? 'column' : 'row' }} spacing={isXS ? 2 : 0} className={classes.root}>
        {/* Left column: title, subtitle, status & availability */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 6
          }}
        >
          <Grid
            container
            style={{ height: '100%' }}
            sx={{
              justifyContent: 'space-between',
              flexDirection: 'column'
            }}
          >
            <Grid className={classes.titleWrapper}>
              <h2 className={classes.title}>{title}</h2>
              {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
            </Grid>
            <Grid container direction="row" spacing={isXS ? 2 : 0}>
              <Grid size={6}>
                <p className={classes.badgeLabel}>STATUS</p>
                <StatusBadge
                  style={{ padding: '1px 4px' }}
                  label={status.label}
                  color={status.bgColor}
                  Icon={() => <status.Icon style={{ width: '28px', height: '28px' }} />}
                  capitalize
                  cornerRounding="max"
                  desktopWidth="120px"
                  align="left"
                />
              </Grid>
              {availability ? (
                <Grid size={6}>
                  <p className={classes.badgeLabel}>AVAILABILITY</p>
                  <StatusBadge
                    style={{ padding: '1px 4px' }}
                    label={availability.label}
                    color={availability.bgColor}
                    fontColor={availability.color}
                    Icon={() => <availability.Icon style={{ width: '28px', height: '28px' }} />}
                    capitalize
                    cornerRounding="max"
                    desktopWidth="120px"
                    align="left"
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        {/* Right column: available for, reserved for, size & low locker */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 6
          }}
        >
          <Grid
            container
            spacing={isXS ? 2 : 0}
            style={{ height: '100%' }}
            sx={{
              justifyContent: 'space-between',
              flexDirection: 'column'
            }}
          >
            {(transactionTypes && transactionTypes.length > 0) ||
            (reservedForRecipients && reservedForRecipients.length > 0) ? (
              <Grid container direction="row">
                {transactionTypes && transactionTypes.length > 0 ? (
                  <Grid size={6}>
                    <Grid className={classes.availableFor}>Available for</Grid>
                    <Grid container direction="row" spacing={1}>
                      {transactionTypes.map((type) => (
                        <Grid key={type.label}>
                          <type.Icon className={classes.availableForIcon} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ) : null}
                {reservedForRecipients && reservedForRecipients.length > 0 ? (
                  <Grid size={6}>
                    <Grid className={classes.availableFor}>Reserved for</Grid>
                    <p className={classes.reservedForNames}>{reservedForRecipients.join(', ')}</p>
                  </Grid>
                ) : null}
              </Grid>
            ) : null}
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Grid size={6}>
                <p className={classes.size}>
                  {size.Icon ? <size.Icon style={{ width: '24px' }} /> : null}
                  {size.label}
                </p>
              </Grid>
              {isConsideredLow ? (
                <Grid size={6}>
                  <p className={classes.lowerLocker}>
                    <ArrowDownward />
                    Low locker
                  </p>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.actionButtonBox}>
        <ActionsButton itemArray={actions} />
      </div>
    </div>
  );
}
